import React, { FC } from 'react';
import { StorageData, useStorage } from '~Components/Storage';
import RadioButtonSelect from '~ComponentsImproved/RadioSelector';
import Toggle from '~ComponentsImproved/Toggle';
import { useLicenses, useLogger } from '~Hooks';
import { SettingsBlock } from '~Scenes/Settings-v2';
import { Box } from '~Styles';
import useI18n from '~Translations';
import { TogglesConfig, isHiddenOnDevice } from '~Scenes/Settings-v2/types';

interface Props {
  /** Set by the Handy shell; the tablet route renders without it */
  isHandy?: boolean;
}

const ReceiptsSettings: FC<Props> = ({ isHandy = false }) => {
  const { i18n } = useI18n();
  const [storage, commitToStorage, storageLoading] = useStorage();
  const logger = useLogger('settings-v2');
  const licenses = useLicenses();

  function updateStorage(data: Partial<StorageData>) {
    logger.info('Updating Settings (Receipt)', { fields: data });

    commitToStorage(data);
  }

  const togglesConfig: Array<TogglesConfig> = [
    {
      label: i18n('Settings.ShortenChequeItemNames'),
      subLabel: i18n('Settings.ShortenChequeItemNames.Text'),
      key: 'truncateReceiptItemNames',
      storage: storage.truncateReceiptItemNames,
    },
    {
      label: i18n('Settings.HideZeroAmountItemsInCustomerPrints.Title'),
      key: 'hideZeroAmountItemsInCustomerPrints',
      storage: storage.hideZeroAmountItemsInCustomerPrints,
    },
    {
      label: i18n('Settings.ReceiptShowOnlyOfferName.Title'),
      subLabel: i18n('Settings.ReceiptShowOnlyOfferName.Description'),
      key: 'receiptShowOnlyOfferName',
      storage: storage.receiptShowOnlyOfferName,
    },
    {
      label: i18n('Settings.ReceiptShowStaffName.Title'),
      subLabel: i18n('Settings.ReceiptShowStaffName.Description'),
      key: 'receiptShowStaffName',
      storage: storage.receiptShowStaffName,
    },
  ];

  if (licenses.hasTSR) {
    togglesConfig.push({
      label: i18n('Settings.ShowTipSuggestions.Title'),
      subLabel: i18n('Settings.ShowTipSuggestions.Description'),
      key: 'showTipSuggestions',
      storage: storage.showTipSuggestions,
    });
  }

  togglesConfig.push({
    label: i18n('Settings.ReceiptTotalAmountLargeFont.Title'),
    subLabel: i18n('Settings.ReceiptTotalAmountLargeFont.Description'),
    key: 'receiptTotalAmountLargeFont',
    storage: storage.receiptTotalAmountLargeFont,
  });

  // Print a separate "Your order number" slip (like the self service kiosks) after each purchase
  togglesConfig.push({
    label: i18n('Settings.PrintOrderNumberSlipAfterReceipt.Title'),
    subLabel: i18n('Settings.PrintOrderNumberSlipAfterReceipt.Description'),
    key: 'printOrderSlip',
    storage: storage.printOrderSlip,
  });

  const radioButtonsConfig: {
    containerLabel: string;
    containerDesc?: string;
    name: string;
    buttons: Array<{
      id: string;
      label: string;
      value: string | null;
    }>;
  }[] = [
    {
      containerLabel: i18n('Settings.ReceiptSettings'),
      containerDesc: i18n('Settings.ReceiptSettings.Desc'),
      name: 'receiptSettings',
      buttons: [
        {
          id: 'ALWAYS',
          label: i18n('Settings.ReceiptSettings.Always'),
          value: 'ALWAYS',
        },
        {
          id: 'ASK',
          label: i18n('Settings.ReceiptSettings.Ask'),
          value: 'ASK',
        },
        {
          id: 'MANUAL',
          label: i18n('Settings.ReceiptSettings.Manual'),
          value: 'MANUAL',
        },
      ],
    },
  ];

  const handlePrintReceiptSetting = (value: string) => {
    if (value === 'ALWAYS') {
      updateStorage({ askToPrint: false, manualReceiptPrint: false });
    }

    if (value === 'ASK') {
      updateStorage({ askToPrint: true, manualReceiptPrint: false });
    }

    if (value === 'MANUAL') {
      updateStorage({ askToPrint: false, manualReceiptPrint: true });
    }
  };

  const printSettingsSelected = storage.manualReceiptPrint
    ? 'MANUAL'
    : storage.askToPrint
    ? 'ASK'
    : 'ALWAYS';

  if (storageLoading) {
    return null;
  }

  return (
    <Box
      padding={isHandy ? 'none' : 'large'}
      marginTop={isHandy ? 'small' : 'none'}
      display="flex"
      flexDirection="column"
      gap={isHandy ? 'small' : 'large'}>
      <SettingsBlock compact={isHandy}>
        {radioButtonsConfig.map((config) => (
          <RadioButtonSelect
            key={config.name}
            settingKey="receiptSettings" // This is actually also manualReceiptPrint key but in backoffice they're set in paralell so this is fine
            data={config}
            selected={printSettingsSelected}
            handleChange={(value) => handlePrintReceiptSetting(value)}
            compact={isHandy}
          />
        ))}
        {togglesConfig.map(
          (toggle) =>
            !isHiddenOnDevice(toggle.hideIn, isHandy) && (
              <Toggle
                key={toggle.key}
                settingKey={toggle.key}
                label={`${toggle.label} ${storage.devMode ? '[' + toggle.key + ']' : ''} `}
                description={toggle.subLabel}
                initialValue={toggle.inverted ? !toggle.storage : toggle.storage}
                onToggle={() => updateStorage({ [toggle.key]: !toggle.storage })}
                fullWidth
                darkBackground
                lightText
                compact={isHandy}
              />
            ),
        )}
      </SettingsBlock>
    </Box>
  );
};

export default ReceiptsSettings;
